import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { keys } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { meterSchema } from '@blimo/shared';
import {
  applyProblemToForm,
  Button,
  Form,
  FormRootError,
  InputField,
  SelectField,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  TextareaField,
  toast,
} from '@blimo/ui';
import { PartyPicker } from './party-picker.jsx';
import { usePropertyOptions } from '../../components/property-options.js';
import { useSupplierOptions } from './utils.js';

const FIELDS = ['property_id', 'number', 'type', 'contract_number', 'supplier_id', 'reader_party_id', 'description'];

/**
 * Zähler anlegen/bearbeiten (`meters.manage`). Cobra-Zähler werden beim nächsten Abgleich überschrieben (Hinweis).
 * @param {{ open: boolean, onOpenChange: (open: boolean) => void, meter?: any, onSaved?: (meter: any) => void }} props
 */
export function MeterSheet({ open, onOpenChange, meter, onSaved }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{meter ? 'Zähler bearbeiten' : 'Zähler anlegen'}</SheetTitle>
          <SheetDescription>
            {meter?.source === 'cobra' ? 'Stammdaten aus Cobra werden beim nächsten Abgleich überschrieben.' : 'Stammdaten des Zählers.'}
          </SheetDescription>
        </SheetHeader>
        {open && <MeterForm meter={meter} onDone={() => onOpenChange(false)} onSaved={onSaved} />}
      </SheetContent>
    </Sheet>
  );
}

function MeterForm({ meter, onDone, onSaved }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { options: lookup } = usePropertyOptions();
  const propertyOptions =
    meter?.property && !lookup.some((o) => o.value === String(meter.property.id))
      ? [{ value: String(meter.property.id), label: meter.property.name ?? `#${meter.property.id}` }, ...lookup]
      : lookup;
  const supplierOptions = useSupplierOptions();
  const form = useForm({
    resolver: zodResolver(meterSchema),
    defaultValues: {
      property_id: meter?.property ? String(meter.property.id) : '',
      number: meter?.number ?? '',
      type: meter?.type ?? '',
      contract_number: meter?.contract_number ?? '',
      supplier_id: meter?.supplier ? String(meter.supplier.id) : '',
      reader_party_id: meter?.reader ? String(meter.reader.id) : '',
      description: meter?.description ?? '',
    },
  });
  const readerId = useWatch({ control: form.control, name: 'reader_party_id' });

  const onSubmit = form.handleSubmit(async (values) => {
    const payload = {
      property_id: Number(values.property_id),
      number: values.number.trim(),
      type: values.type.trim() || null,
      contract_number: values.contract_number.trim() || null,
      supplier_id: values.supplier_id ? Number(values.supplier_id) : null,
      reader_party_id: values.reader_party_id ? Number(values.reader_party_id) : null,
      description: values.description.trim() || null,
    };
    try {
      const saved = meter ? await api.admin.meters.update(meter.id, payload) : await api.admin.meters.create(payload);
      queryClient.invalidateQueries({ queryKey: keys.admin.resource('meters') });
      queryClient.invalidateQueries({ queryKey: keys.admin.resource('meter-readings') });
      toast.success(meter ? 'Zähler gespeichert' : 'Zähler angelegt');
      onDone();
      onSaved?.(saved);
    } catch (error) {
      applyProblemToForm(error, form.setError, { fields: FIELDS });
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} noValidate className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
        <FormRootError />
        <SelectField name="property_id" label="Liegenschaft" options={propertyOptions} placeholder="Bitte wählen …" />
        <InputField name="number" label="Zählernummer" autoComplete="off" />
        <InputField name="type" label="Zählerart" description="z. B. Strom, Wasser, Gas" />
        <InputField name="contract_number" label="Vertragsnummer" />
        <SelectField name="supplier_id" label="Versorger" options={supplierOptions} placeholder="Kein Versorger" />
        <PartyPicker
          label="Ableser"
          value={readerId}
          valueLabel={meter?.reader && String(meter.reader.id) === readerId ? meter.reader.name : undefined}
          emptyLabel="Kein Ableser"
          onChange={(id) => form.setValue('reader_party_id', id, { shouldDirty: true })}
        />
        <TextareaField name="description" label="Beschreibung" rows={3} />
        <SheetFooter className="px-0">
          <Button type="submit" loading={form.formState.isSubmitting}>
            {meter ? 'Speichern' : 'Anlegen'}
          </Button>
          <Button type="button" variant="outline" onClick={onDone}>
            Abbrechen
          </Button>
        </SheetFooter>
      </form>
    </Form>
  );
}
